import { isArray, mapValues } from "lodash";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { ProfileItem } from "PFApp/components/profile_item";
import { useGrowl } from "PFApp/use_growl";
import PageBox from "PFComponents/box/page_box";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import AutoSelect from "PFComponents/select/autoselect";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import api from "PFCore/api";
import { customTypesForProfile } from "PFCore/helpers/custom_types";
import safePrune from "PFCore/helpers/safe_prune";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import useWindowSize from "PFCore/helpers/use_window_size";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { fetchProfilesOptions } from "PFCore/services/profile";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import css from "./edit_collaborators_configs_page.modules.scss";
import ProfilesList from "./profiles_list";

const Error = ({ error }) => <div className={css.error}>{error.join(", ")}</div>;

const EditCollaboratorsConfigsPage = ({ collaboratorConfig, id }) => {
  const { t } = useTranslation("collaboratorsConfigs", { keyPrefix: "edit" });
  const { windowWidth } = useWindowSize();
  const { data: currentProfile } = useCurrentProfile();
  const { customTypes } = useCustomTypes();

  const customValueById = (name) =>
    (collaboratorConfig?.customValues || []).filter(
      // ? added to find to prevent non existoing cf's to break the page
      (item) => item.customTypeId === customTypes.find((item) => item.name === name)?.id
    );

  const permittedCustomTypes = useMemo(
    () => customTypesForProfile(currentProfile, currentProfile, customTypes),
    [customTypes, currentProfile]
  );

  const getCustomType = (name) => permittedCustomTypes.find((item) => item.name === name);

  const ErrorCodes = useMemo(
    () => ({
      "name": t("name"),
      "custom_value_ids": t("customValues"),
      "profile_ids": t("profile"),
      "custom_value_ids[custom_values]": ""
    }),
    []
  );

  const [collaborators, setCollaborators] = useState(collaboratorConfig?.profiles || []);
  const [name, setName] = useState(collaboratorConfig?.name || "");
  const [businessUnit, setBusinessUnit] = useState(customValueById("business_unit") || []);
  const [businessArea, setBusinessArea] = useState(customValueById("service_line") || []);
  const [businessLine, setBusinessLine] = useState(customValueById("business_line") || []);
  const [capability, setCapability] = useState(customValueById("capability") || []);
  const [talentModel, setTalentModel] = useState(customValueById("talent_model") || []);
  const [clientSector, setClientSector] = useState(customValueById("client_sector") || []);
  const [errors, setErrors] = useState({});

  const history = useHistory();
  const growl = useGrowl();

  const businessCustomType = getCustomType("business_unit");
  const businessAreaCustomType = getCustomType("service_line");
  const businessLineCustomType = getCustomType("business_line");
  const capabilityCustomType = getCustomType("capability");
  const talentModelCustomType = getCustomType("talent_model");
  const clientSectorCustomType = getCustomType("client_sector");

  const { getFormattedErrors } = useResponseErrors();

  const saveConfig = () => {
    const payload = {
      name: name,
      profileIds: collaborators.map(({ id }) => id),
      customValueIds: [
        businessUnit[0]?.id,
        businessArea[0]?.id,
        businessLine[0]?.id,
        capability[0]?.id,
        talentModel[0]?.id,
        clientSector[0]?.id
      ].filter((item) => item)
    };

    api({
      url: `admin/participation_rules${id ? `/${id}` : ""}`,
      method: id ? "PUT" : "POST",
      data: payload
    })
      .then(() => {
        growl({ message: t("success") });
        history.push("/admin/collaborators_configs");
      })
      .catch(({ response }) => {
        const formatedErrors = mapValues(getFormattedErrors(response), (val) =>
          isArray(val) ? val.join(", ") : val
        );
        setErrors(formatedErrors);

        if (response.status === 500) {
          return growl({
            message: t("translation:somethingWentWrong"),
            kind: "error"
          });
        }
        Object.keys(formatedErrors).map((item) => {
          growl({
            message: `${t("translation:somethingWentWrong")}. ${ErrorCodes[item]}${ErrorCodes[item] && ":"} ${
              formatedErrors[item]
            }`,
            kind: "error"
          });
        });
      });
  };

  const CollaboratorsTitle = safePrune(t("collaborators"), windowWidth < 500 ? 5 : 100);

  return (
    <div className={css.wrapper}>
      <h1 className={css.header}>{id ? t("editConfig") : t("newConfig")}</h1>
      <PageBox style={{ padding: 20, marginBottom: 40 }}>
        <div className={css.lineWrapper}>
          <div className={css.left}>{t("name")}</div>
          <div className={css.right}>
            <InputFieldSet value={name} onChange={setName} error={errors?.name} />
          </div>
        </div>
        <div className={css.lineWrapper}>
          <div className={css.left}>{CollaboratorsTitle}</div>
          <div className={css.right}>
            <div>
              <AutoSelect
                qaId="collaborators-config-selection"
                values={collaborators}
                label={t("selectTheProfiles")}
                multi
                alwaysEmpty
                query={(term) => fetchProfilesOptions({ term })}
                formatOption={(item) => ({
                  id: item.id,
                  displayElement: <ProfileItem profile={item} />,
                  item: item
                })}
                filterOptions={(options) => {
                  const selectedCollaborators = collaborators.map((item) => item.id);
                  return options.filter(
                    (option) => currentProfile.id !== option.id && !selectedCollaborators.includes(option.id)
                  );
                }}
                error={errors.profile_ids}
                handleChange={(profile) => {
                  setCollaborators((prev) => [
                    ...prev.filter((item) => item.id !== profile[0].id),
                    ...profile
                  ]);
                }}
                letClear
                closeOnChange
              />
              <ProfilesList
                profiles={collaborators}
                onRemove={(id) => setCollaborators((prev) => prev.filter((item) => item.id !== id))}
              />
            </div>
          </div>
        </div>
        {businessCustomType && (
          <div className={css.lineWrapper}>
            <div className={css.left}>{businessCustomType.display_as}</div>
            <div className={css.right}>
              <CustomValuesEditField
                label={businessCustomType.display_as}
                customType={businessCustomType}
                values={businessUnit}
                qaIdPrefix="business-configuration"
                errors={errors.custom_value_ids && businessUnit.length === 0 && errors.custom_value_ids}
                profile={currentProfile}
                handleChange={setBusinessUnit}
                adminPage={false}
                letClear
                letCreate={false}
              />
            </div>
          </div>
        )}
        {businessAreaCustomType && (
          <div className={css.lineWrapper}>
            <div className={css.left}>{businessAreaCustomType.display_as}</div>
            <div className={css.right}>
              <CustomValuesEditField
                label={businessAreaCustomType.display_as}
                customType={businessAreaCustomType}
                values={businessArea}
                qaIdPrefix="business-area-configuration"
                errors={errors.custom_value_ids && businessArea.length === 0 && errors.custom_value_ids}
                profile={currentProfile}
                handleChange={setBusinessArea}
                adminPage={false}
                letClear
                letCreate={false}
              />
            </div>
          </div>
        )}
        {businessLineCustomType && (
          <div className={css.lineWrapper}>
            <div className={css.left}>{businessLineCustomType.display_as}</div>
            <div className={css.right}>
              <CustomValuesEditField
                label={businessLineCustomType.display_as}
                customType={businessLineCustomType}
                values={businessLine}
                qaIdPrefix="business-line-configuration"
                errors={errors.custom_value_ids && businessLine.length === 0 && errors.custom_value_ids}
                profile={currentProfile}
                handleChange={setBusinessLine}
                adminPage={false}
                letClear
                letCreate={false}
              />
            </div>
          </div>
        )}
        {capabilityCustomType && (
          <div className={css.lineWrapper}>
            <div className={css.left}>{capabilityCustomType.display_as}</div>
            <div className={css.right}>
              <CustomValuesEditField
                label={capabilityCustomType.display_as}
                customType={capabilityCustomType}
                values={capability}
                qaIdPrefix="capability-configuration"
                errors={errors.custom_value_ids && capability.length === 0 && errors.custom_value_ids}
                profile={currentProfile}
                handleChange={setCapability}
                adminPage={false}
                letClear
                letCreate={false}
              />
            </div>
          </div>
        )}
        {talentModelCustomType && (
          <div className={css.lineWrapper}>
            <div className={css.left}>{talentModelCustomType.display_as}</div>
            <div className={css.right}>
              <CustomValuesEditField
                label={talentModelCustomType.display_as}
                customType={talentModelCustomType}
                values={talentModel}
                qaIdPrefix="talent-model-configuration"
                errors={errors.custom_value_ids && talentModel.length === 0 && errors.custom_value_ids}
                profile={currentProfile}
                handleChange={setTalentModel}
                adminPage={false}
                letClear
                letCreate={false}
              />
            </div>
          </div>
        )}
        {clientSectorCustomType && (
          <div className={css.lineWrapper}>
            <div className={css.left}>{clientSectorCustomType.display_as}</div>
            <div className={css.right}>
              <CustomValuesEditField
                label={clientSectorCustomType.display_as}
                customType={clientSectorCustomType}
                values={clientSector}
                qaIdPrefix="client-sector-configuration"
                errors={errors.custom_value_ids && clientSector.length === 0 && errors.custom_value_ids}
                profile={currentProfile}
                handleChange={setClientSector}
                adminPage={false}
                letClear
                letCreate={false}
              />
            </div>
          </div>
        )}
        <div className={css.footer}>
          <ButtonLink kind="secondary" href="/admin/collaborators_configs">
            {t("translation:cancel")}
          </ButtonLink>
          <Button onClick={saveConfig}>{id ? t("updateConfig") : t("createConfig")}</Button>
        </div>
      </PageBox>
    </div>
  );
};

EditCollaboratorsConfigsPage.propTypes = {
  collaboratorConfig: PropTypes.shape({
    name: PropTypes.string,
    customValues: PropTypes.array,
    profiles: PropTypes.array,
    id: PropTypes.number
  }),
  fetchCollaboratorsConfigs: PropTypes.func,
  id: PropTypes.string
};

Error.propTypes = {
  error: PropTypes.array
};

export default EditCollaboratorsConfigsPage;
